import React, { useEffect, useState } from 'react';
import { applyTypography, BaseButton, Input } from '@repo/common';

/**
 * Create Notice Modal
 *
 * Figma: Create a notice
 * - 상단: 좌측 close 텍스트 버튼, 중앙 타이틀, 우측 Submit 버튼
 * - 본문: Notice title (필수), Add details (optional) textarea + 글자수 카운트
 *
 * @param {boolean} isOpen - 모달 열림 여부
 * @param {function} onClose - 닫기 콜백
 * @param {function} onSubmit - 제출 콜백 ({ title, details }) 인자를 받음
 */
const CreateNoticeModal = ({
  isOpen,
  onClose,
  onSubmit,
  initialTitle = '',
  initialDetails = '',
  modalTitle = 'Create a notice',
  submitLabel = 'Submit',
}) => {
  const [title, setTitle] = useState(initialTitle);
  const [details, setDetails] = useState(initialDetails);

  const MAX_DETAILS_LENGTH = 500;
  const isSubmitDisabled = title.trim().length === 0;

  useEffect(() => {
    if (!isOpen) return;
    setTitle(initialTitle);
    setDetails(initialDetails);
  }, [initialDetails, initialTitle, isOpen]);

  if (!isOpen) return null;

  const handleClose = (event) => {
    event?.preventDefault();
    event?.stopPropagation();
    onClose && onClose();
  };

  const handleContainerClick = (e) => {
    // 컨테이너 내부 클릭 시 오버레이 닫힘 방지
    e.stopPropagation();
  };

  const handleSubmit = () => {
    if (isSubmitDisabled) return;

    onSubmit && onSubmit({ title: title.trim(), details: details.trim() });
    onClose && onClose();
  };

  return (
    <>
      {/* Overlay */}
      <div
        className='fixed inset-0 bg-surface-black40 z-[9998]'
        onClick={handleClose}
      />

      {/* Modal Container */}
      <div className='fixed inset-0 flex items-center justify-center z-[9999] px-[24px] py-[24px]'>
        <div
          className='bg-grayscale-gray01 flex flex-col items-center w-full max-w-[840px] h-[560px] rounded-[30px] shadow-2'
          onClick={handleContainerClick}
        >
          {/* Header */}
          <div className='flex items-center w-full gap-[8px] px-[24px] pt-[20px] pb-[16px]'>
            {/* Left: close text */}
            <div className='flex items-center flex-1 p-4'>
              <BaseButton
                type='text'
                size='s'
                htmlType='button'
                hideIcon
                typo={false}
                isStatic
                onClick={handleClose}
                textWrapperClass='h-auto'
                className={`${applyTypography(
                  'b14Strong',
                  'close',
                )} h-auto min-w-0 cursor-pointer rounded-none border-0 bg-transparent p-0 text-green-deep03 hover:text-green-deep04 transition-colors`}
              >
                close
              </BaseButton>
            </div>

            {/* Center: title */}
            <p
              className={`flex-1 text-center ${applyTypography(
                'b16Strong',
                modalTitle,
              )} text-text-primary`}
            >
              {modalTitle}
            </p>

            {/* Right: submit button */}
            <div className='flex justify-end flex-1 px-4 py-3'>
              <BaseButton
                type='primary'
                size='m'
                hideIcon
                isDisabled={isSubmitDisabled}
                onClick={handleSubmit}
                width='120px'
              >
                {submitLabel}
              </BaseButton>
            </div>
          </div>

          {/* Body */}
          <div className='flex flex-col flex-1 w-full px-[40px] pb-[30px]'>
            {/* Notice title */}
            <div className='w-full pb-[16px]'>
              <Input
                label=''
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder='Notice title*'
                maxLength={80}
                hideCount
              />
            </div>

            {/* Details textarea */}
            <div className='flex-1 w-full'>
              <Input
                as='textarea'
                label=''
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                placeholder='Add details (optional)'
                maxLength={MAX_DETAILS_LENGTH}
                textareaMinHeight='280px'
                textareaRows={8}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CreateNoticeModal;
